import { Box, Button, MenuItem, Stack, TextField } from "@mui/material";
import { useMemo, useState } from "react";
import { PagedTable, type Column } from "../../components/PagedTable";
import { useClergy, type ClergyQuery } from "./api";
import type { ClergyRecord } from "../../types/directory";
import { useDebounced } from "../../hooks/useDebounced";
import { EmptyState } from "../../components/EmptyState";
import { DEFAULT_PAGE_SIZE } from "../../lib/pagination";
import { usePageTitle } from "../../app/usePageTitle";

const DEFAULT: ClergyQuery = { search: "", page: 1, page_size: DEFAULT_PAGE_SIZE };

const dash = (value: string | null | undefined) => value ?? "--";

const COLUMNS: Column<ClergyRecord>[] = [
  { key: "jurisdiction", label: "Jurisdiction", render: (row) => row.jurisdiction?.name ?? "--" },
  { key: "diocese", label: "Diocese", render: (row) => row.diocese?.name ?? "--" },
  { key: "salutation", label: "Salutation", render: (row) => dash(row.salutation) },
  { key: "rank", label: "Rank", render: (row) => dash(row.rank) },
  { key: "position", label: "Position", render: (row) => dash(row.position) },
  { key: "legal_first_name", label: "First name", render: (row) => dash(row.legal_first_name) },
  { key: "legal_last_name", label: "Last name", render: (row) => dash(row.legal_last_name) },
  { key: "status", label: "Status", render: (row) => dash(row.status) },
  { key: "notes", label: "Notes", render: (row) => dash(row.notes) },
  { key: "on_loan_from", label: "On loan from", render: (row) => row.on_loan_from ?? "--" },
];

export function ClergyPage() {
  usePageTitle("Clergy");
  const [query, setQuery] = useState<ClergyQuery>(DEFAULT);
  const [search, setSearch] = useState("");
  useDebounced(search, 300, (term) =>
    setQuery((previous) => ({ ...previous, search: term, page: 1 })),
  );

  const filtered = query.search !== "" || Boolean(query.jurisdiction) || Boolean(query.diocese);
  const clergy = useClergy(query);
  const rows = useMemo(() => clergy.data?.clergy_directory ?? [], [clergy.data]);

  // The endpoint returns no facet list, so the filters offer what is on the
  // current page -- the same thing the legacy screen does.
  const jurisdictions = useMemo(
    () => [...new Set(rows.map((row) => row.jurisdiction?.name).filter(Boolean))] as string[],
    [rows],
  );
  const dioceses = useMemo(
    () => [...new Set(rows.map((row) => row.diocese?.name).filter(Boolean))] as string[],
    [rows],
  );

  return (
    <Box>
      <Stack direction="row" sx={{ gap: 2, mb: 2, flexWrap: "wrap" }}>
        <TextField
          label="Search"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          sx={{ minWidth: 240 }}
        />
        <TextField
          select
          label="Jurisdiction"
          value={query.jurisdiction ?? ""}
          onChange={(event) =>
            setQuery((previous) => ({
              ...previous,
              jurisdiction: event.target.value || undefined,
              page: 1,
            }))
          }
          sx={{ minWidth: 220 }}
        >
          <MenuItem value="">All</MenuItem>
          {jurisdictions.map((name) => (
            <MenuItem key={name} value={name}>
              {name}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          select
          label="Diocese"
          value={query.diocese ?? ""}
          onChange={(event) =>
            setQuery((previous) => ({
              ...previous,
              diocese: event.target.value || undefined,
              page: 1,
            }))
          }
          sx={{ minWidth: 220 }}
        >
          <MenuItem value="">All</MenuItem>
          {dioceses.map((name) => (
            <MenuItem key={name} value={name}>
              {name}
            </MenuItem>
          ))}
        </TextField>
        <Button
          onClick={() => {
            setSearch("");
            setQuery(DEFAULT);
          }}
        >
          Reset
        </Button>
      </Stack>

      <PagedTable
        columns={COLUMNS}
        rows={rows}
        rowKey={(row) => row.id ?? `${row.legal_first_name}-${row.legal_last_name}-${row.rank}`}
        total={clergy.data?.total_records ?? 0}
        page={query.page - 1}
        pageSize={query.page_size}
        loading={clergy.isFetching}
        empty={
          <EmptyState
            filtered={filtered}
            noun="clergy records"
            onClearFilters={() => {
              setSearch("");
              setQuery(DEFAULT);
            }}
          />
        }
        onPageChange={(page) => setQuery((previous) => ({ ...previous, page: page + 1 }))}
        onPageSizeChange={(page_size) =>
          setQuery((previous) => ({ ...previous, page_size, page: 1 }))
        }
      />
    </Box>
  );
}
